<template>
  <div class="org-capacity-addon">
    <div class="addon-head">
      <div>
        <span>HRM_ORG_CAPACITY_HEALTH</span>
        <h2>组织编制健康洞察</h2>
        <p>聚合部门层级、在职规模、空部门、超编风险和组织配置缺口，辅助管理层判断组织承载健康度。</p>
      </div>
      <el-button type="primary" icon="el-icon-refresh" :loading="loading" @click="loadData">刷新</el-button>
    </div>

    <el-row :gutter="16" class="metric-row">
      <el-col v-for="item in metrics" :key="item.label" :xs="24" :sm="12" :lg="6">
        <div class="metric-card" :class="item.tone">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <em>{{ item.meta }}</em>
        </div>
      </el-col>
    </el-row>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="14">
        <el-card shadow="never">
          <div slot="header">部门容量排行</div>
          <el-table :data="overview.deptCapacityRanking" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="parent_dept_name" label="上级" min-width="120" />
            <el-table-column prop="active_staff_count" label="在职" width="80" />
            <el-table-column prop="resigned_staff_count" label="离职" width="80" />
            <el-table-column prop="disabled_staff_count" label="禁用" width="80" />
            <el-table-column prop="health_status" label="状态" width="110">
              <template slot-scope="{ row }">{{ formatHealth(row.health_status) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="10">
        <el-card shadow="never">
          <div slot="header">人员状态分布</div>
          <div class="status-list">
            <div v-for="item in overview.staffStatusDistribution" :key="item.status" class="status-item">
              <span>{{ formatStaffStatus(item.status) }}</span>
              <strong>{{ item.staff_count }}</strong>
              <em>员工</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">部门配置缺口</div>
          <el-table :data="overview.deptConfigGapList" size="small" v-loading="loading">
            <el-table-column prop="dept_name" label="部门" min-width="120" />
            <el-table-column prop="parent_dept_name" label="上级" min-width="120" />
            <el-table-column prop="missing_code" label="编码缺口" width="90">
              <template slot-scope="{ row }">{{ row.missing_code ? '是' : '否' }}</template>
            </el-table-column>
            <el-table-column prop="missing_work_time" label="工时缺口" width="90">
              <template slot-scope="{ row }">{{ row.missing_work_time ? '是' : '否' }}</template>
            </el-table-column>
            <el-table-column prop="missing_schedule" label="排班缺口" width="90">
              <template slot-scope="{ row }">{{ row.missing_schedule ? '是' : '否' }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">顶级部门管理跨度</div>
          <el-table :data="overview.parentSpanDistribution" size="small" v-loading="loading">
            <el-table-column prop="dept_group" label="部门组" min-width="130" />
            <el-table-column prop="child_dept_count" label="子部门" width="90" />
            <el-table-column prop="staff_count" label="覆盖在职" width="100" />
          </el-table>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="lower-row">
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">未挂有效部门员工</div>
          <el-table :data="overview.orphanStaffList" size="small" v-loading="loading" empty-text="暂无异常">
            <el-table-column prop="code" label="工号" width="100" />
            <el-table-column prop="staff_name" label="姓名" min-width="120" />
            <el-table-column prop="dept_id" label="部门ID" width="90" />
            <el-table-column prop="status" label="状态" width="90">
              <template slot-scope="{ row }">{{ formatStaffStatus(row.status) }}</template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="12">
        <el-card shadow="never">
          <div slot="header">入职趋势</div>
          <div class="trend-list">
            <div v-for="item in overview.monthlyJoinTrend" :key="item.month_label" class="trend-item">
              <span>{{ item.month_label }}</span>
              <strong>{{ item.join_count }}</strong>
              <em>新增员工</em>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getOrgCapacityHealthOverview } from '../api/orgCapacityHealth'

export default {
  name: 'OrgCapacityHealthOverview',
  data () {
    return {
      loading: false,
      overview: {
        deptCount: 0,
        leafDeptCount: 0,
        activeStaffCount: 0,
        inactiveStaffCount: 0,
        emptyDeptCount: 0,
        overCapacityDeptCount: 0,
        deptCapacityRanking: [],
        parentSpanDistribution: [],
        orphanStaffList: [],
        deptConfigGapList: [],
        staffStatusDistribution: [],
        monthlyJoinTrend: []
      }
    }
  },
  computed: {
    metrics () {
      return [
        { label: '部门总数', value: this.overview.deptCount, meta: `${this.overview.leafDeptCount || 0} 个叶子部门` },
        { label: '在职员工', value: this.overview.activeStaffCount, meta: `${this.overview.inactiveStaffCount || 0} 个非在职`, tone: 'blue' },
        { label: '空部门', value: this.overview.emptyDeptCount, meta: '需清理或合并', tone: 'orange' },
        { label: '超编风险', value: this.overview.overCapacityDeptCount, meta: '单部门 >= 8 人', tone: 'red' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    loadData () {
      this.loading = true
      getOrgCapacityHealthOverview().then(response => {
        if (response.code === 200) {
          this.overview = Object.assign({}, this.overview, response.data || {})
        } else {
          this.$message.warning(response.message || '组织编制健康洞察加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    formatHealth (status) {
      const map = {
        EMPTY: '空部门',
        OVER_CAPACITY: '疑似超编',
        UNDER_CAPACITY: '人员偏少',
        HEALTHY: '健康'
      }
      return map[status] || '未知'
    },
    formatStaffStatus (status) {
      const map = { 0: '离职', 1: '在职', 2: '禁用' }
      return map[Number(status)] || '未知'
    }
  }
}
</script>

<style lang="less" scoped>
.org-capacity-addon {
  padding: 20px;
  background: #f5f7fb;
  min-height: 100%;
}

.addon-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 22px 24px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.addon-head span {
  display: block;
  margin-bottom: 8px;
  color: #0f766e;
  font-size: 12px;
  font-weight: 700;
}

.addon-head h2 {
  margin: 0 0 8px;
  color: #17233d;
  font-size: 24px;
}

.addon-head p {
  margin: 0;
  color: #5f6f89;
}

.metric-row {
  margin-bottom: 16px;
}

.metric-card {
  min-height: 118px;
  padding: 18px;
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
}

.metric-card span,
.metric-card em {
  display: block;
  color: #6b7a90;
  font-style: normal;
}

.metric-card strong {
  display: block;
  margin: 10px 0 8px;
  color: #17233d;
  font-size: 30px;
  line-height: 1;
}

.metric-card.red strong {
  color: #d93026;
}

.metric-card.orange strong {
  color: #d9822b;
}

.metric-card.blue strong {
  color: #2f6fec;
}

.lower-row {
  margin-top: 16px;
}

.status-list,
.trend-list {
  display: grid;
  gap: 10px;
}

.status-item,
.trend-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #edf1f7;
  border-radius: 6px;
}

.status-item span,
.status-item em,
.trend-item span,
.trend-item em {
  color: #5f6f89;
  font-style: normal;
}

.status-item strong,
.trend-item strong {
  color: #17233d;
  font-size: 16px;
}
</style>
